// Artist-credit parsing + grouping, ported from the mobile app
// (flacmusic-app/src/artists/parse-artists.ts + build-artist-groups.ts) so the web
// browse groups artists the same way: case/diacritic/feat variants of one act collapse
// to a single primary artist ("JVKE" === "Jvke", "Thang" absorbs "Thang, Ha Le"), and a
// track's PRIMARY (first-credited) artist is what decides which artist/album it belongs to.

// Placeholder that stands in for the comma inside ", Jr."-style suffixes while the string
// is split on real separators, then swapped back. A plain-ASCII token (via split/join, not
// a regex) — no realistic name contains it, and it avoids the control-char regex the app
// used.
const SUFFIX_TOKEN = '__FLACDIUM_SUFFIX_COMMA__';
// ", Jr." / ", Sr." / ", II" / ", III" / ", Jnr" — protect from the comma split.
const SUFFIX_GUARD_RE = /,(\s*(?:Jr\.?|Sr\.?|II|III|Jnr)\b)/gi;
// `;`, feat/ft/featuring, spaced `x`, and `,` (suffix-protected above).
const SPLIT_RE = /\s*;\s*|\s+(?:feat\.?|ft\.?|featuring)\s+|\s+x\s+|\s*,\s*/gi;

/** Lowercase + strip diacritics/punctuation merge key, so "JVKE" / "Jvke" / " jvke "
 * all collapse to the same key. */
export function normalizeArtistKey(name) {
    return (name || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // combining diacritical marks
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

/** Split a raw credit string into individual artist names; element 0 is the PRIMARY
 * artist. Returns [] only for empty/whitespace input. */
export function splitArtistCredits(raw) {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return [];
    const guarded = trimmed.replace(SUFFIX_GUARD_RE, (_m, tail) => `${SUFFIX_TOKEN}${tail}`);
    const parts = guarded
        .split(SPLIT_RE)
        .map((p) => p.split(SUFFIX_TOKEN).join(',').trim())
        .filter((p) => p.length > 0);
    return parts.length > 0 ? parts : [trimmed];
}

/** Normalized merge key of a credit string's PRIMARY artist — the value album/artist
 * grouping compares on ("Thang, Ha Le" and "Thang" both key to "thang"). */
export function primaryArtistKey(raw) {
    const segs = splitArtistCredits(raw);
    return normalizeArtistKey(segs[0] ?? raw ?? '');
}

/**
 * Collapse a raw artist roster (each item {name, picture, _tracks, …}) into primary-artist
 * groups. `makeId(name)` mints the routing id for the group's canonical spelling (kept as
 * the app does: the variant with the most tracks). Track counts sum; the first non-empty
 * picture represents the group. Returns items in the same shape as the input, in the same
 * relative order the server returned them (Map insertion order) so the caller's sort control
 * (recent/plays/name) isn't silently discarded.
 */
export function buildArtistGroups(rawItems, makeId) {
    const groups = new Map();
    for (const a of rawItems || []) {
        const primary = splitArtistCredits(a.name)[0] || a.name || '';
        const normalizedKey = normalizeArtistKey(primary);
        // Names that normalize to nothing (punctuation-only, empty) still need a group —
        // keyed off the raw text (not one shared bucket) so distinct oddball names don't
        // silently collapse into a single merged card.
        const key = normalizedKey || `__unknown__${primary}`;
        let g = groups.get(key);
        if (!g) {
            g = { variants: new Map(), picture: null, tracks: 0 };
            groups.set(key, g);
        }
        const n = a._tracks || 0;
        const variantName = primary || 'Unknown Artist';
        g.variants.set(variantName, (g.variants.get(variantName) || 0) + n);
        g.tracks += n;
        if (!g.picture && a.picture) g.picture = a.picture;
    }
    const out = [];
    for (const g of groups.values()) {
        let bestName = '';
        let bestCount = -1;
        for (const [name, count] of g.variants) {
            if (count > bestCount) {
                bestName = name;
                bestCount = count;
            }
        }
        out.push({ id: makeId(bestName), name: bestName, picture: g.picture, _tracks: g.tracks });
    }
    return out;
}

/**
 * Collapse a raw album roster (each item {title, artist, cover, type, numberOfTracks})
 * into one card per (exact album name + PRIMARY artist) — so a single album whose tracks
 * carry feat/split-artist credits (e.g. "Thang", "Thang, Ha Le", "Thang, Suni" all for the
 * same album) shows as ONE card, while a same-named album by a genuinely different artist
 * (e.g. "Kế Phúc") stays a separate card. Track counts sum; the canonical artist spelling
 * is the primary variant with the most tracks. `makeId(albumTitle, artistName)` mints the
 * routing id (the album detail then re-groups by the same rule). Server order preserved.
 */
export function buildAlbumGroups(rawItems, makeId) {
    const groups = new Map();
    for (const a of rawItems || []) {
        const rawTitle = a.title || '';
        const normalizedTitle = normalizeArtistKey(rawTitle);
        const artistPrimary = splitArtistCredits(a.artist || '')[0] || a.artist || '';
        const normalizedArtist = normalizeArtistKey(artistPrimary);
        // Empty title/artist keys fall back to their raw text (not one shared bucket) so
        // distinct untitled/unattributed albums don't silently merge into a single card.
        const titleKey = normalizedTitle || `__untitled__${rawTitle}`;
        const artistKey = normalizedArtist || `__unknown__${artistPrimary}`;
        const key = titleKey + '|' + artistKey;
        let g = groups.get(key);
        if (!g) {
            g = {
                title: rawTitle || 'Unknown Album',
                artistVariants: new Map(),
                cover: null,
                tracks: 0,
                hasFull: false,
                hasEp: false,
            };
            groups.set(key, g);
        }
        const n = a.numberOfTracks || 0;
        const artistVariantName = artistPrimary || 'Unknown Artist';
        g.artistVariants.set(artistVariantName, (g.artistVariants.get(artistVariantName) || 0) + n);
        g.tracks += n;
        if (!g.cover && a.cover) g.cover = a.cover;
        if (a.type === 'EP') g.hasEp = true;
        else g.hasFull = true;
    }
    const out = [];
    for (const g of groups.values()) {
        let bestArtist = '';
        let bestCount = -1;
        for (const [name, count] of g.artistVariants) {
            if (count > bestCount) {
                bestCount = count;
                bestArtist = name;
            }
        }
        out.push({
            id: makeId(g.title, bestArtist),
            title: g.title,
            cover: g.cover,
            artist: bestArtist,
            type: g.hasFull ? 'ALBUM' : 'EP',
            numberOfTracks: g.tracks,
        });
    }
    return out;
}
